'use client'

import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { IReservation } from '@/_service/types/reservations'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { formatReservationDate } from '../../_utils/formatDate'
import { format } from 'date-fns'
import { enUS, ru, uz } from 'date-fns/locale';
import { BRAND_NAMES } from '@/_utils/Constants'

const localeMap = {
  ru,
  en: enUS,
  uz,
};

const statusColors = {
  new: 'blue.500',
  finished: 'green.500',
  canceled: 'red.500',
} as const

type StatusType = keyof typeof statusColors

export default function ReservationCard({ data }: { data: IReservation }) {
  const t = useTranslations()
  const locale = useCustomLocale();

  return (
    <Box
      py={{ base: '4', lg: '5' }}
      px={{ base: '3', lg: '4' }}
      rounded='20px'
      gap='10px'
      bg='background.card'
      border='1px solid'
      borderColor='transparent'
      _hover={{ borderColor: 'primary' }}
      cursor='pointer'
      transition='all 0.3s ease'
    >
      <HStack w='full' justifyContent='space-between' mb={3}>
        <VStack alignItems='flex-start' gap='1'>
          <Text
            fontSize={{ base: '18px', lg: '20px' }}
            fontWeight='bold'
            lineHeight='120%'
            color='text'
          >
            {BRAND_NAMES[data?.shipper_id]}
          </Text>
          <Text
            fontSize={{ base: '14px', lg: '15px' }}
            lineHeight='120%'
            fontWeight='medium'
            color='#667085'
          >
            {/* {order?.created_at ? format(order?.created_at, 'dd MMM HH:mm') : ''} */}
            {data?.created_at
              ? format(new Date(data.created_at), 'dd MMM HH:mm', {
                locale: localeMap[locale as keyof typeof localeMap],
              })
              : ''}
          </Text>
        </VStack>

        <VStack gap='0' alignItems='flex-end'>
          <Text
            fontSize={{ base: '16px', lg: '18px' }}
            lineHeight='120%'
            fontWeight='bold'
            color='text'
            letterSpacing='1%'
          >
            #{data?.external_reserve_id}
          </Text>

          <Text
            textAlign='end'
            fontSize={{ base: '13px', lg: '15px' }}
            lineHeight='22px'
            fontWeight='medium'
            color={data?.status ? statusColors[data?.status as StatusType] : ''}
          >
            {t(`status_${data?.status}`)}
          </Text>
        </VStack>
      </HStack>
      <Text
        mb={1}
        fontSize={14}
        fontWeight={500}
        lineHeight='20px'
      >
        {`${t('branch')}: ${data?.branch_name}`}
      </Text>
      <Text
        mb={1}
        fontSize={14}
        fontWeight={500}
        lineHeight='20px'
      >
        {`${t('section')}: ${data?.section?.name?.[locale]}, ${t('table_stol')} - ${data?.table?.number}`}
      </Text>
      <Text
        mb={1}
        fontSize={14}
        fontWeight={500}
        lineHeight='20px'
      >
        {`${t('reserved_time')}: ${formatReservationDate(
          data?.reserve_date,
          data?.start_time,
          data?.end_time,
          locale
        )}`}
      </Text>
    </Box>
  )
}
